/**
 * SchedulerNewTaskForm
 * Inline slide-down form for creating an ad-hoc task from the Task Scheduler page.
 * Calls useCreateTask() on submit and collapses itself via the taskUIStore.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled, { keyframes } from 'styled-components';
import { X, Plus } from 'lucide-react';
import { useCreateTask } from '../../hooks/useTasks';
import { useClientList } from '../../hooks/useClients';
import { useTaskUIStore } from '../../stores/taskUIStore';
import userService from '../../services/api/userService';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { formatPersonMeta } from '../../components/shared/PersonMeta';
import type { TaskType, CreateTaskDto } from '../../types/tasks';

// ============================================================================
// Constants
// ============================================================================

const TASK_TYPE_OPTIONS: { value: TaskType; label: string }[] = [
  { value: 'CLINICAL_FORM', label: 'Clinical Form' },
  { value: 'REVIEW', label: 'Review' },
  { value: 'MEETING', label: 'Meeting' },
  { value: 'AD_HOC', label: 'Ad Hoc' },
];

const initialFormState = {
  name: '',
  type: 'AD_HOC' as TaskType,
  assignedToUserId: '',
  scheduledDate: '',
  dueDate: '',
  description: '',
  clientMemberId: '',
};

// ============================================================================
// Animations
// ============================================================================

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============================================================================
// Styled components
// ============================================================================

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing[5]};
  animation: ${slideDown} 200ms ease-out;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FormTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroupFull = styled.div`
  grid-column: 1 / -1;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  margin-left: 2px;
`;

const inputStyles = ({ theme }: { theme: any }) => `
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text.primary};
  background: ${theme.colors.backgrounds.white};
  border: 1px solid ${theme.colors.borders.medium};
  border-radius: ${theme.borderRadius.md};
  transition: border-color ${theme.transitions.fast}, box-shadow ${theme.transitions.fast};
  box-sizing: border-box;

  &::placeholder {
    color: ${theme.colors.text.light};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.base};
    box-shadow: ${theme.shadows.ring};
  }

  &:disabled {
    background: ${theme.colors.backgrounds.gray};
    color: ${theme.colors.text.light};
    cursor: not-allowed;
  }
`;

const StyledInput = styled.input`
  ${inputStyles}
`;

const StyledSelect = styled.select`
  ${inputStyles}
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: ${({ theme }) => theme.spacing[8]};
`;

const StyledTextarea = styled.textarea`
  ${inputStyles}
  resize: vertical;
  min-height: 72px;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: ${({ theme }) => `${theme.spacing[1]} 0 0`};
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[5]};
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    border-color: ${({ theme }) => theme.colors.borders.dark};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.white};
  background: ${({ theme }) => theme.colors.primary.base};
  border: 1px solid ${({ theme }) => theme.colors.primary.base};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary.dark};
    border-color: ${({ theme }) => theme.colors.primary.dark};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ServerErrorBanner = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  margin: ${({ theme }) => `${theme.spacing[4]} 0 0`};
`;

// ============================================================================
// Validation
// ============================================================================

interface FormErrors {
  name?: string;
  assignedToUserId?: string;
  scheduledDate?: string;
}

function validate(values: typeof initialFormState): FormErrors {
  const errors: FormErrors = {};
  if (!values.name.trim()) errors.name = 'Task name is required.';
  if (!values.assignedToUserId.trim()) errors.assignedToUserId = 'Assignee is required.';
  if (!values.scheduledDate) errors.scheduledDate = 'Scheduled date is required.';
  return errors;
}

// ============================================================================
// Component
// ============================================================================

export function SchedulerNewTaskForm() {
  const { toggleSchedulerForm } = useTaskUIStore();
  const { mutateAsync: createTask, isPending, isError, error } = useCreateTask();
  // A new task is new clinical work: Discharged and Inactive clients aren't offered
  // (ADO #585). The rule lives on the server, so this form doesn't hard-code statuses.
  const { data: clientsData } = useClientList({ limit: 100, workflowEligible: true });
  const clients = clientsData?.data ?? [];

  // Assignee search is server-side (ADO 406): the member list is the one unbounded
  // list here, and the old `limit: 200` fetch silently truncated past that.
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const debouncedAssigneeSearch = useDebouncedValue(assigneeSearch.trim(), 300);

  const { data: membersData, isFetching: isFetchingMembers } = useQuery({
    queryKey: ['members', 'for-tasks', debouncedAssigneeSearch],
    queryFn: () =>
      userService.getMembers({
        limit: 25,
        status: 'active',
        ...(debouncedAssigneeSearch ? { search: debouncedAssigneeSearch } : {}),
      }),
    placeholderData: (prev) => prev,
  });

  const memberOptions = useMemo(() => {
    const members = membersData?.data ?? [];
    return members
      .filter((m) => m.isActive)
      .map((m) => ({
        value: m.userId,
        label: `${m.firstName} ${m.lastName}`,
        meta: formatPersonMeta({
          centerName: m.primaryCenter?.name ?? null,
          roleName: m.roles?.[0]?.name ?? null,
        }),
      }));
  }, [membersData]);

  const [values, setValues] = useState(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate({ ...values });
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name as keyof FormErrors] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = Object.fromEntries(
      Object.keys(values).map((k) => [k, true]),
    );
    setTouched(allTouched);
    const fieldErrors = validate(values);
    setErrors(fieldErrors);

    if (Object.keys(fieldErrors).length > 0) return;

    const dto: CreateTaskDto = {
      name: values.name.trim(),
      type: values.type,
      assignedToUserId: values.assignedToUserId.trim(),
      scheduledDate: values.scheduledDate,
      ...(values.dueDate ? { dueDate: values.dueDate } : {}),
      ...(values.description.trim() ? { description: values.description.trim() } : {}),
      ...(values.clientMemberId ? { clientMemberId: values.clientMemberId } : {}),
    };

    try {
      await createTask(dto);
      toggleSchedulerForm();
    } catch {
      // Error is surfaced via `isError` from the mutation
    }
  };

  const serverErrorMessage =
    isError && error instanceof Error ? error.message : isError ? 'Failed to create task.' : null;

  return (
    <FormCard role="region" aria-label="New task form">
      <FormHeader>
        <FormTitle>New Task</FormTitle>
        <CloseButton
          onClick={toggleSchedulerForm}
          type="button"
          aria-label="Close new task form"
        >
          <X size={16} />
        </CloseButton>
      </FormHeader>

      <form onSubmit={handleSubmit} noValidate>
        <FieldGrid>
          {/* Task name */}
          <FieldGroupFull>
            <FieldGroup>
              <Label htmlFor="task-name">
                Task Name<RequiredMark aria-hidden="true">*</RequiredMark>
              </Label>
              <StyledInput
                id="task-name"
                name="name"
                type="text"
                placeholder="e.g. Fidelity Check"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                aria-required="true"
                aria-describedby={errors.name ? 'name-error' : undefined}
                aria-invalid={!!errors.name}
              />
              {errors.name && touched.name && (
                <ErrorText id="name-error" role="alert">
                  {errors.name}
                </ErrorText>
              )}
            </FieldGroup>
          </FieldGroupFull>

          {/* Task type */}
          <FieldGroup>
            <Label htmlFor="task-type">Task Type</Label>
            <StyledSelect
              id="task-type"
              name="type"
              value={values.type}
              onChange={handleChange}
            >
              {TASK_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </StyledSelect>
          </FieldGroup>

          {/* Client (optional) */}
          <FieldGroup>
            <Label htmlFor="task-client">Client (optional)</Label>
            <StyledSelect
              id="task-client"
              name="clientMemberId"
              value={values.clientMemberId}
              onChange={handleChange}
            >
              <option value="">No client</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}
                </option>
              ))}
            </StyledSelect>
          </FieldGroup>

          {/* Assignee */}
          <FieldGroup>
            {/* No htmlFor: SearchableSelect is a button+listbox, labelled via aria-label. */}
            <Label as="span">
              Assignee<RequiredMark aria-hidden="true">*</RequiredMark>
            </Label>
            <SearchableSelect
              value={values.assignedToUserId}
              options={memberOptions}
              onChange={(userId) => {
                setValues((prev) => ({ ...prev, assignedToUserId: userId }));
                setTouched((prev) => ({ ...prev, assignedToUserId: true }));
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.assignedToUserId;
                  return next;
                });
              }}
              onSearchChange={setAssigneeSearch}
              isLoading={isFetchingMembers}
              placeholder="Select assignee..."
              emptyText="No members match your search."
              aria-label="Assignee"
            />
            {errors.assignedToUserId && touched.assignedToUserId && (
              <ErrorText id="assignee-error" role="alert">
                {errors.assignedToUserId}
              </ErrorText>
            )}
          </FieldGroup>

          {/* Scheduled date */}
          <FieldGroup>
            <Label htmlFor="task-scheduled-date">
              Scheduled Date<RequiredMark aria-hidden="true">*</RequiredMark>
            </Label>
            <StyledInput
              id="task-scheduled-date"
              name="scheduledDate"
              type="date"
              value={values.scheduledDate}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-required="true"
              aria-describedby={errors.scheduledDate ? 'scheduled-date-error' : undefined}
              aria-invalid={!!errors.scheduledDate}
            />
            {errors.scheduledDate && touched.scheduledDate && (
              <ErrorText id="scheduled-date-error" role="alert">
                {errors.scheduledDate}
              </ErrorText>
            )}
          </FieldGroup>

          {/* Due date (optional) */}
          <FieldGroup>
            <Label htmlFor="task-due-date">Due Date (optional)</Label>
            <StyledInput
              id="task-due-date"
              name="dueDate"
              type="date"
              value={values.dueDate}
              onChange={handleChange}
            />
          </FieldGroup>

          {/* Description (optional) */}
          <FieldGroupFull>
            <FieldGroup>
              <Label htmlFor="task-description">Description (optional)</Label>
              <StyledTextarea
                id="task-description"
                name="description"
                placeholder="Add context or notes for this task..."
                value={values.description}
                onChange={handleChange}
              />
            </FieldGroup>
          </FieldGroupFull>
        </FieldGrid>

        {serverErrorMessage && (
          <ServerErrorBanner role="alert">{serverErrorMessage}</ServerErrorBanner>
        )}

        <ActionRow>
          <CancelButton type="button" onClick={toggleSchedulerForm}>
            Cancel
          </CancelButton>
          <CreateButton type="submit" disabled={isPending}>
            <Plus size={14} aria-hidden="true" />
            {isPending ? 'Creating...' : 'Create Task'}
          </CreateButton>
        </ActionRow>
      </form>
    </FormCard>
  );
}

export default SchedulerNewTaskForm;
